// Bildaufnahme und -komprimierung vor dem Hochladen.
// Ein iPhone-Foto hat gern 4 MB; für die Erkennung reichen 1600 px Kantenlänge.

export interface CompressOptions {
  maxEdge?: number;
  quality?: number;
}

export async function compressImage(
  file: File | Blob,
  { maxEdge = 1600, quality = 0.82 }: CompressOptions = {},
): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Bild konnte nicht verarbeitet werden.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  );
  if (!blob) throw new Error('Bild konnte nicht komprimiert werden.');
  return blob;
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Bild konnte nicht gelesen werden.'));
    reader.readAsDataURL(blob);
  });
}

/** Öffnet die Kamera bzw. Dateiauswahl und liefert das Bild als Data-URL. */
export function pickImage(): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.style.display = 'none';
    document.body.append(input);

    // Bricht der Nutzer ab, feuert `change` nie – deshalb zusätzlich auf die
    // Rückkehr ins Fenster hören, sonst hinge das Versprechen für immer.
    const cleanup = () => {
      input.remove();
      window.removeEventListener('focus', onFocus);
    };
    let settled = false;
    const onFocus = () => {
      window.setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(null);
      }, 800);
    };

    input.addEventListener('change', () => {
      settled = true;
      const file = input.files?.[0] ?? null;
      cleanup();
      if (!file) {
        resolve(null);
        return;
      }
      void compressImage(file)
        .then(blobToDataUrl)
        .then(resolve)
        .catch(() => resolve(null));
    });

    window.addEventListener('focus', onFocus);
    input.click();
  });
}
